import React, { useState, useEffect } from 'react';
import { reportService, doctorService } from '@/services/api';
import { useClinic } from '@/contexts/ClinicContext';
import { useAuth } from '@/contexts/AuthContext';
import { getKolkataTodayString, formatTime, kolkataToUtcIso, utcToKolkataParts } from '@/utils/dateTime';
import { toast } from 'sonner';
import {
  BarChart3,
  Calendar,
  Download,
  Filter,
  Users,
  CheckCircle2,
  Clock,
  TrendingUp,
  Activity,
  Stethoscope,
  Building2,
  FileSpreadsheet,
  FileText,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const ReportsPage = () => {
  const { activeClinicId, availableClinics } = useClinic();
  const { hasPermission } = useAuth();
  const canViewAllClinics = hasPermission('CLINIC_ALL_VIEW') || hasPermission('PLATFORM_MANAGE');

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [selectedClinic, setSelectedClinic] = useState(canViewAllClinics ? 'all' : (activeClinicId || 'all'));
  const [selectedDoctor, setSelectedDoctor] = useState('all');
  const [dateRange, setDateRange] = useState('7d'); // 'today' | '7d' | '30d'
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadDoctors();
  }, []);

  useEffect(() => {
    loadReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClinic, selectedDoctor, dateRange, activeClinicId]);

  const loadDoctors = async () => {
    try {
      const res = await doctorService.getAll();
      setDoctors(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const loadReportData = async () => {
    try {
      setLoading(true);
      const todayStr = getKolkataTodayString();
      const todayUtc = new Date(kolkataToUtcIso(todayStr, '12:00:00')).getTime();

      let startDate = todayStr;
      if (dateRange === '7d') {
        const p = utcToKolkataParts(new Date(todayUtc - 7 * 86400000));
        startDate = p ? p.dateString : todayStr;
      } else if (dateRange === '30d') {
        const p = utcToKolkataParts(new Date(todayUtc - 30 * 86400000));
        startDate = p ? p.dateString : todayStr;
      }

      const report = await reportService.getAnalytics({
        clinicId: selectedClinic !== 'all' ? selectedClinic : undefined,
        doctorId: selectedDoctor !== 'all' ? selectedDoctor : undefined,
        startDate,
        endDate: todayStr,
      });
      setData(report);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (formatType = 'csv') => {
    try {
      setExporting(true);
      const res = await reportService.exportReport(formatType);
      toast.success(`Report exported successfully as ${res.filename}`);
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-7xl" data-testid="reports-page">
      {/* Header & Export Action */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">
            Clinical Operations & Reports
          </h1>
          <p className="text-slate-500 text-sm">
            Outpatient appointments, consultation volumes, and doctor productivity metrics
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={exporting}
            onClick={() => handleExport('csv')}
            className="border-slate-300 text-xs font-semibold"
          >
            <FileSpreadsheet className="h-4 w-4 mr-1.5 text-emerald-600" />
            Export CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={exporting}
            onClick={() => handleExport('pdf')}
            className="border-slate-300 text-xs font-semibold"
          >
            <FileText className="h-4 w-4 mr-1.5 text-rose-600" />
            Export PDF
          </Button>
        </div>
      </div>

      {/* Filter Bar */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-3">
            {/* Date Range Selector */}
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="h-9 w-[150px] text-xs">
                <SelectValue placeholder="Date Range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="7d">Last 7 Days</SelectItem>
                <SelectItem value="30d">Last 30 Days</SelectItem>
              </SelectContent>
            </Select>

            {/* Clinic Filter (Only if user has CLINIC_ALL_VIEW) */}
            {canViewAllClinics && (
              <Select value={selectedClinic} onValueChange={setSelectedClinic}>
                <SelectTrigger className="h-9 w-[180px] text-xs">
                  <SelectValue placeholder="All Clinics" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Hospital Clinics</SelectItem>
                  {availableClinics?.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Doctor Filter */}
            <Select value={selectedDoctor} onValueChange={setSelectedDoctor}>
              <SelectTrigger className="h-9 w-[180px] text-xs">
                <SelectValue placeholder="All Doctors" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Doctors</SelectItem>
                {doctors.map(d => (
                  <SelectItem key={d.id} value={d.id}>Dr. {d.firstName} {d.lastName}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Badge variant="outline" className="bg-slate-50 text-slate-600 text-xs">
            Refreshed: {formatTime(new Date())}
          </Badge>
        </CardContent>
      </Card>

      {loading || !data ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-28 rounded-xl" />)}
        </div>
      ) : (
        <>
          {/* Top KPI Metrics Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border-slate-200">
              <CardContent className="p-5 flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Appointments</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{data.summary.totalAppointments}</p>
                  <p className="text-[11px] text-teal-600 mt-0.5">Across assigned clinics</p>
                </div>
                <div className="h-11 w-11 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center">
                  <Calendar className="h-5 w-5" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardContent className="p-5 flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Consultations Completed</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{data.summary.completedAppointments}</p>
                  <p className="text-[11px] text-emerald-600 mt-0.5">{data.summary.completionRate}% completion rate</p>
                </div>
                <div className="h-11 w-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
                  <CheckCircle2 className="h-5 w-5" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardContent className="p-5 flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Patients Managed</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{data.summary.totalPatients}</p>
                  <p className="text-[11px] text-blue-600 mt-0.5">Active medical records</p>
                </div>
                <div className="h-11 w-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                  <Users className="h-5 w-5" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardContent className="p-5 flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Avg Waiting Time</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{data.summary.avgWaitTimeMinutes}m</p>
                  <p className="text-[11px] text-amber-600 mt-0.5">From check-in to consult</p>
                </div>
                <div className="h-11 w-11 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
                  <Clock className="h-5 w-5" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Visual Analytics Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Weekly Volume Bar Chart (8 cols) */}
            <Card className="lg:col-span-8 border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-bold text-slate-900">
                  Weekly Appointment & Consultation Volume
                </CardTitle>
                <CardDescription className="text-xs">
                  Daily comparison of bookings vs completed clinical encounters
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="h-72 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.weeklyTrends} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                      <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="appointments" fill="#0d9488" name="Booked Visits" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="consultations" fill="#10b981" name="Completed Encounters" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Appointment Status Distribution Donut Chart (4 cols) */}
            <Card className="lg:col-span-4 border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-bold text-slate-900">
                  Visit Status Breakdown
                </CardTitle>
                <CardDescription className="text-xs">
                  Distribution of appointment outcomes
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-2">
                <div className="h-56 w-full flex items-center justify-center">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={data.statusDistribution}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={75}
                        paddingAngle={4}
                        dataKey="value"
                      >
                        {data.statusDistribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs pt-2">
                  {data.statusDistribution.map((s, idx) => (
                    <div key={idx} className="flex items-center gap-1.5">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: s.color }} />
                      <span className="text-slate-600">{s.name}: <strong>{s.value}</strong></span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Doctor-wise Performance Table */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-bold text-slate-900">
                Doctor Productivity & Consultation Volume
              </CardTitle>
              <CardDescription className="text-xs">
                Physician encounter metrics and estimated clinical revenue
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 text-xs">
                    <TableHead>Physician Name</TableHead>
                    <TableHead>Specialization</TableHead>
                    <TableHead className="text-center">Total Scheduled</TableHead>
                    <TableHead className="text-center">Completed Consultations</TableHead>
                    <TableHead className="text-right">Consultation Revenue (₹)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.doctorStats.map((doc) => (
                    <TableRow key={doc.id} className="text-xs hover:bg-slate-50/50">
                      <TableCell className="font-semibold text-slate-900">{doc.name}</TableCell>
                      <TableCell className="text-slate-600">{doc.specialization}</TableCell>
                      <TableCell className="text-center font-medium">{doc.totalAppointments}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">
                          {doc.completedConsultations} Completed
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-bold text-slate-900">
                        ₹{doc.revenue.toLocaleString('en-IN')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default ReportsPage;

